import type { FunctionalComponent } from 'preact';
import { env } from '@/lib/env.ts';
import { isInAppBrowser } from '@/lib/inapp.ts';
import { getInflow } from '@/lib/inflow.ts';
import { openOauthPopup, readReturnTo, saveOauthReturn } from '@/lib/oauth.ts';

interface Props {
  onFail?: (message: string) => void;
}

/**
 * 로그인 방식.
 *   p  팝업 — 결과를 원래 창에 postMessage 로 넘긴다
 *   r  페이지 이동 — 백엔드가 사이트로 1회용 티켓을 들려 되돌려 보낸다
 */
type Mode = 'p' | 'r';

/**
 * 카카오 인증 주소.
 *
 * ★ `state` 는 `<방식>.<유입코드>` 다(`r.dongguk`). 카카오는 이 값을 **손대지 않고
 *   그대로 돌려주므로** 백엔드가 어떻게 응답할지(팝업 화면 / 사이트로 되돌리기)와
 *   유입 경로를 함께 알 수 있다. 해석은 백엔드 `parseKakaoState`.
 */
function authorizeUrl(mode: Mode): string {
  const state = `${mode}.${getInflow() ?? ''}`;

  return (
    'https://kauth.kakao.com/oauth/authorize' +
    `?client_id=${encodeURIComponent(env.kakao.clientId)}` +
    `&redirect_uri=${encodeURIComponent(env.kakao.api)}` +
    '&response_type=code' +
    `&state=${encodeURIComponent(state)}`
  );
}

/**
 * 카카오 로그인 버튼.
 *
 * redirect_uri 는 백엔드의 팝업 엔드포인트(VITE_KAKAO_API)다. 카카오가 그쪽으로
 * code 를 넘기면 백엔드가 client_secret 으로 토큰을 교환한다. 프론트는 secret 을
 * 만지지 않는다.
 *
 * ★ **인앱 브라우저에서는 팝업을 쓰지 않는다.** (2026-09-17)
 *   카카오톡 안의 브라우저는 팝업을 진짜 창으로 만들지 않아, 팝업이 결과를 넘길
 *   원래 창이 없다. "원래 화면을 찾을 수 없습니다" 가 뜨고, 그 창을 닫으면 사이트가
 *   통째로 사라졌다. 그래서 페이지 전체를 카카오로 보내고, 백엔드가 사이트로
 *   되돌려 보내게 한다.
 */
export const KakaoLoginButton: FunctionalComponent<Props> = ({ onFail }) => {
  function handleClick() {
    if (!env.kakao.clientId || !env.kakao.api) {
      onFail?.('카카오 로그인이 설정되지 않았습니다.');
      return;
    }

    // 어느 방식이든 돌아갈 곳을 맡겨 둔다. 페이지 이동이면 `?r=` 이 사라지고,
    // 팝업이 원래 창을 못 찾아 스스로 돌아가는 경우(비상구)에도 이 값을 쓴다.
    saveOauthReturn(readReturnTo());

    if (isInAppBrowser()) {
      window.location.assign(authorizeUrl('r'));
      return;
    }

    /*
      ★ 팝업이 막혀도 멈추지 않는다. 예전에는 "팝업이 차단되었습니다" 로 끝났는데,
        페이지 이동 방식으로 이어 가면 사용자가 브라우저 설정을 만질 필요가 없다.
    */
    const popup = openOauthPopup(authorizeUrl('p'), 'kakaoLogin');
    if (!popup) window.location.assign(authorizeUrl('r'));
  }

  return (
    <button type="button" class="login-btn login-btn--kakao" onClick={handleClick}>
      <span class="login-btn-icon">
        <img src="/images/kakao.png" alt="" />
      </span>
      카카오 로그인
    </button>
  );
};
